// YERİ: src/app/domains/website/sections/mkt-app-mock.ts
//
// Vitrinin ürün görselleri. Ekran görüntüsü DOSYASI değil — KODLA çizilir.
//
// Neden: demo GIF'leri hazır olmadan yayına çıkıyoruz ve o zamana kadar her
// medya yuvası boş kesikli bir kutuydu. Sayfa baştan aşağı gri dikdörtgen =
// wireframe görüntüsü. Kodla çizilen mockup hem boşluğu kapatır hem de ürünün
// NE OLDUĞUNU gösterir: kenar çubuğu, takvim, müşteri kartı, fatura.
//
// Ek faydası: sıfır dosya, sıfır ağ isteği, tam SSR uyumlu (crawler mockup'ı
// HTML'de görür).
//
// HER ZAMAN AÇIK TEMA (bkz. styles/marketing.css → .mkt-mock): ürünün gerçek
// varsayılan teması açık, ve koyu hero'nun üstünde açık bir pencere "ekranın
// içinden çıkmış" gibi durur. Koyu üstüne koyu, ürünü zemine gömerdi.
//
// Metinler SAYFANIN DİLİNDE: İngilizce bir sayfada Türkçe bir arayüz göstermek
// güveni doğrudan zedeler — ziyaretçi ürünün kendi dilinde olmadığını sanır.

import { Component, computed, inject, input } from '@angular/core';
import { LanguageStore } from '../core/language.store';

export type AppMockVariant =
  | 'dashboard'
  | 'calendar'
  | 'crm'
  | 'invoice'
  | 'website';

@Component({
  selector: 'mkt-app-mock',
  standalone: true,
  template: `
    <div class="mkt-mock relative select-none" aria-hidden="true">
      <div class="overflow-hidden rounded-2xl border border-[color:var(--mkt-border)] bg-[color:var(--mkt-surface)] shadow-2xl shadow-neutral-950/25">

        <!-- Pencere başlığı -->
        <div class="flex items-center gap-2 border-b border-[color:var(--mkt-border)] bg-[color:var(--mkt-paper-soft)] px-4 py-2.5">
          <span class="size-2.5 rounded-full bg-[#ff5f57]"></span>
          <span class="size-2.5 rounded-full bg-[#febc2e]"></span>
          <span class="size-2.5 rounded-full bg-[#28c840]"></span>
          <!-- Adres çubuğu da site fontunda (Figtree). Bir dönem monospace'ti —
               gerçek tarayıcılar öyle gösterdiği için — ama vitrinde tek başına
               yabancı bir font kalıyordu. Sayfada logo yazısı dışında tek font
               var. Harf aralığı biraz açıldı: küçük puntoda URL'in "yazılmış"
               hissini monospace veriyordu, onu tracking ile telafi ediyoruz. -->
          <span class="ms-3 rounded-md bg-[color:var(--mkt-surface)] px-2.5 py-0.5 text-[10px] tracking-wide text-[color:var(--mkt-text-faint)]">
            app.melbarrel.com/{{ route() }}
          </span>
        </div>

        <div class="flex">
          <!-- Kenar çubuğu -->
          <div class="hidden w-40 shrink-0 border-e border-[color:var(--mkt-border)] bg-[color:var(--mkt-paper-soft)] p-3 sm:block">
            <div class="mb-4 flex items-center gap-2 px-1">
              <span class="grid size-6 place-items-center rounded-md text-[10px] font-bold text-white"
                style="background: var(--mkt-gradient)">M</span>
              <span class="text-xs font-semibold">Melbarrel</span>
            </div>

            @for (item of navItems(); track item.label) {
              <div class="mb-0.5 flex items-center gap-2 rounded-lg px-2 py-1.5 text-[11px] font-medium"
                [class]="item.active
                  ? 'bg-[color:var(--mkt-accent)]/10 text-[color:var(--mkt-accent)]'
                  : 'text-[color:var(--mkt-text-faint)]'">
                <span class="size-1.5 rounded-full"
                  [class]="item.active ? 'bg-[color:var(--mkt-accent)]' : 'bg-current opacity-40'"></span>
                {{ item.label }}
              </div>
            }
          </div>

          <!-- İçerik -->
          <div class="min-w-0 flex-1 p-4">
            @switch (variant()) {

              @case ('dashboard') {
                <div class="mb-4 flex items-center justify-between">
                  <p class="text-sm font-semibold">{{ t().today }}</p>
                  <span class="rounded-md px-2 py-0.5 text-[10px] font-semibold text-white"
                    style="background: var(--mkt-gradient)">{{ t().live }}</span>
                </div>

                <div class="mb-4 grid grid-cols-3 gap-2">
                  @for (kpi of t().kpis; track kpi.label) {
                    <div class="rounded-xl border border-[color:var(--mkt-border)] p-2.5">
                      <p class="text-[9px] uppercase tracking-wide text-[color:var(--mkt-text-faint)]">{{ kpi.label }}</p>
                      <p class="mt-0.5 text-lg font-bold tracking-tight">{{ kpi.value }}</p>
                      <p class="text-[9px] font-medium text-emerald-600">{{ kpi.delta }}</p>
                    </div>
                  }
                </div>

                <!-- Gelir grafiği: saf CSS sütunlar, görsel yok -->
                <div class="rounded-xl border border-[color:var(--mkt-border)] p-3">
                  <p class="mb-3 text-[10px] font-medium text-[color:var(--mkt-text-faint)]">{{ t().revenue }}</p>
                  <div class="flex h-20 items-end gap-1.5">
                    @for (h of bars; track $index) {
                      <div class="flex-1 rounded-t-[3px]"
                        [style.height.%]="h"
                        [style.background]="$index === bars.length - 1
                          ? 'var(--mkt-gradient)'
                          : 'color-mix(in srgb, var(--mkt-accent) 20%, transparent)'"></div>
                    }
                  </div>
                </div>
              }

              @case ('calendar') {
                <div class="mb-3 flex items-center justify-between">
                  <p class="text-sm font-semibold">{{ t().appointments }}</p>
                  <span class="rounded-md border border-[color:var(--mkt-border)] px-2 py-0.5 text-[10px] text-[color:var(--mkt-text-faint)]">
                    {{ t().thisWeek }}
                  </span>
                </div>

                <div class="grid grid-cols-4 gap-1.5">
                  @for (day of t().days; track day.name) {
                    <div>
                      <p class="mb-1.5 text-center text-[9px] font-semibold text-[color:var(--mkt-text-faint)]">{{ day.name }}</p>
                      <div class="space-y-1.5">
                        @for (slot of day.slots; track $index) {
                          <div class="rounded-lg p-1.5 text-[9px] leading-tight"
                            [class]="slot.hot
                              ? 'text-white'
                              : 'border border-[color:var(--mkt-border)] text-[color:var(--mkt-text-muted)]'"
                            [style.background]="slot.hot ? 'var(--mkt-gradient)' : ''">
                            <span class="block font-semibold">{{ slot.time }}</span>
                            <span class="block opacity-80">{{ slot.who }}</span>
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>
              }

              @case ('crm') {
                <div class="mb-3 flex items-center gap-2.5">
                  <span class="grid size-9 place-items-center rounded-full text-xs font-bold text-white"
                    style="background: var(--mkt-gradient)">{{ t().avatar }}</span>
                  <div>
                    <p class="text-sm font-semibold">{{ t().customerName }}</p>
                    <p class="text-[10px] text-[color:var(--mkt-text-faint)]">{{ t().customerSince }}</p>
                  </div>
                  <span class="ms-auto rounded-md bg-emerald-500/10 px-2 py-0.5 text-[9px] font-semibold text-emerald-600">
                    {{ t().active }}
                  </span>
                </div>

                <div class="mb-3 flex gap-1">
                  @for (tab of t().crmTabs; track tab; let first = $first) {
                    <span class="rounded-md px-2 py-1 text-[10px] font-medium"
                      [class]="first
                        ? 'bg-[color:var(--mkt-accent)]/10 text-[color:var(--mkt-accent)]'
                        : 'text-[color:var(--mkt-text-faint)]'">{{ tab }}</span>
                  }
                </div>

                <div class="space-y-1.5">
                  @for (row of t().timeline; track row.title) {
                    <div class="flex items-center gap-2.5 rounded-lg border border-[color:var(--mkt-border)] p-2">
                      <span class="size-1.5 shrink-0 rounded-full bg-[color:var(--mkt-accent)]"></span>
                      <div class="min-w-0 flex-1">
                        <p class="truncate text-[11px] font-medium">{{ row.title }}</p>
                        <p class="text-[9px] text-[color:var(--mkt-text-faint)]">{{ row.meta }}</p>
                      </div>
                      <span class="shrink-0 text-[10px] font-semibold">{{ row.amount }}</span>
                    </div>
                  }
                </div>
              }

              @case ('invoice') {
                <div class="mb-3 flex items-start justify-between">
                  <div>
                    <p class="text-sm font-semibold">{{ t().invoiceNo }}</p>
                    <p class="text-[10px] text-[color:var(--mkt-text-faint)]">{{ t().invoiceMeta }}</p>
                  </div>
                  <span class="rounded-md bg-emerald-500/10 px-2 py-1 text-[9px] font-bold text-emerald-600">
                    {{ t().paid }}
                  </span>
                </div>

                <div class="rounded-xl border border-[color:var(--mkt-border)]">
                  @for (line of t().invoiceLines; track line.label) {
                    <div class="flex items-center justify-between border-b border-[color:var(--mkt-border)] px-3 py-2">
                      <span class="text-[11px] text-[color:var(--mkt-text-muted)]">{{ line.label }}</span>
                      <span class="text-[11px] font-medium">{{ line.value }}</span>
                    </div>
                  }
                  <div class="flex items-center justify-between rounded-b-xl px-3 py-2.5"
                    style="background: color-mix(in srgb, var(--mkt-accent) 8%, transparent)">
                    <span class="text-[11px] font-semibold">{{ t().total }}</span>
                    <span class="text-base font-bold tracking-tight">{{ t().totalValue }}</span>
                  </div>
                </div>

                <div class="mt-3 flex items-center gap-2 rounded-lg border border-[color:var(--mkt-border)] p-2">
                  <span class="grid size-6 place-items-center rounded-md text-white"
                    style="background: var(--mkt-gradient)">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </span>
                  <p class="text-[10px] text-[color:var(--mkt-text-muted)]">{{ t().paidBy }}</p>
                </div>
              }

              @case ('website') {
                <div class="mb-3 flex items-center justify-between">
                  <p class="text-sm font-semibold">{{ t().website }}</p>
                  <span class="rounded-md px-2 py-0.5 text-[10px] font-semibold text-white"
                    style="background: var(--mkt-gradient)">{{ t().published }}</span>
                </div>

                <div class="flex gap-2">
                  <div class="w-24 shrink-0 space-y-1.5">
                    @for (block of t().siteBlocks; track block; let i = $index) {
                      <div class="flex items-center gap-1.5 rounded-lg border border-dashed border-[color:var(--mkt-border)] px-2 py-1.5 text-[9px] font-medium text-[color:var(--mkt-text-faint)]"
                        [class]="i === 3 ? '!border-solid !border-[color:var(--mkt-accent)] !text-[color:var(--mkt-accent)]' : ''">
                        <span class="size-1 rounded-full bg-current"></span>{{ block }}
                      </div>
                    }
                  </div>

                  <div class="flex-1 space-y-1.5 rounded-xl border border-[color:var(--mkt-border)] p-2">
                    <div class="h-3 rounded bg-[color:var(--mkt-border)]"></div>
                    <div class="h-8 rounded-lg" style="background: var(--mkt-gradient)"></div>
                    <div class="grid grid-cols-3 gap-1.5">
                      @for (i of [1, 2, 3]; track i) {
                        <div class="h-8 rounded border border-[color:var(--mkt-border)]"></div>
                      }
                    </div>
                    <div class="h-6 rounded-lg border-2 border-[color:var(--mkt-accent)]"></div>
                  </div>
                </div>
              }
            }
          </div>
        </div>
      </div>
    </div>
  `,
})
export class MktAppMock {
  readonly variant = input<AppMockVariant>('dashboard');

  private langStore = inject(LanguageStore);

  protected t = computed(() => STRINGS[this.langStore.current()] ?? STRINGS['en']);

  /** Gelir grafiği sütun yükseklikleri (%) — son sütun vurgulu (bu ay). */
  protected bars = [38, 52, 44, 61, 55, 72, 68, 84, 79, 92];

  protected navItems = computed(() => {
    const active = this.variant();
    const order: AppMockVariant[] = ['dashboard', 'calendar', 'crm', 'invoice', 'website'];

    return this.t().nav.map((label, index) => ({
      label,
      active: order[index] === active,
    }));
  });

  protected route = computed(() => {
    const map: Record<AppMockVariant, string> = {
      dashboard: 'portal/dashboard',
      calendar:  'portal/booking',
      crm:       'portal/crm/contacts',
      invoice:   'portal/erp/invoices',
      website:   'portal/web-builder',
    };
    return map[this.variant()];
  });
}

interface MockStrings {
  today: string;
  live: string;
  revenue: string;
  appointments: string;
  thisWeek: string;
  customerName: string;
  avatar: string;
  customerSince: string;
  active: string;
  crmTabs: string[];
  invoiceNo: string;
  invoiceMeta: string;
  paid: string;
  total: string;
  totalValue: string;
  paidBy: string;
  website: string;
  published: string;
  siteBlocks: string[];
  /** Kenar çubuğu — SIRA ÖNEMLİ: dashboard, calendar, crm, invoice, website, … */
  nav: string[];
  kpis: { label: string; value: string; delta: string }[];
  days: { name: string; slots: { time: string; who: string; hot: boolean }[] }[];
  timeline: { title: string; meta: string; amount: string }[];
  invoiceLines: { label: string; value: string }[];
}

const STRINGS: Record<string, MockStrings> = {
  en: {
    today: 'Today',
    live: 'Live',
    revenue: 'Monthly revenue',
    appointments: 'Appointments',
    thisWeek: 'This week',
    customerName: 'Sarah Whitfield',
    avatar: 'SW',
    customerSince: 'Customer · since 2019',
    active: 'Active',
    crmTabs: ['History', 'Documents', 'Payments', 'Notes'],
    invoiceNo: 'Invoice #2026-0148',
    invoiceMeta: 'Sarah Whitfield · Net 14',
    paid: 'PAID',
    total: 'Total',
    totalValue: '$4,850',
    paidBy: 'Paid by card · 12 Jul, 14:32',
    website: 'Website',
    published: 'Live',
    siteBlocks: ['Navbar', 'Hero', 'Services', 'Booking', 'Footer'],
    nav: ['Dashboard', 'Calendar', 'Customers', 'Invoices', 'Website', 'Team', 'Settings'],
    kpis: [
      { label: 'Bookings', value: '18',     delta: '+12%' },
      { label: 'Revenue',  value: '$32.4K', delta: '+8%'  },
      { label: 'New',      value: '7',      delta: '+3'   },
    ],
    days: [
      { name: 'Mon', slots: [{ time: '09:00', who: 'S. Whitfield', hot: false }, { time: '11:30', who: 'M. Cole', hot: false }] },
      { name: 'Tue', slots: [{ time: '10:00', who: 'J. Rivera', hot: true }, { time: '15:00', who: 'T. Novak', hot: false }] },
      { name: 'Wed', slots: [{ time: '09:30', who: 'A. Bishop', hot: false }] },
      { name: 'Thu', slots: [{ time: '13:00', who: 'D. Fraser', hot: false }, { time: '16:30', who: 'P. Lange', hot: false }] },
    ],
    timeline: [
      { title: 'Follow-up appointment', meta: '12 Jul · Dr. Hayes',   amount: '$450'   },
      { title: 'Treatment plan signed', meta: '28 Jun · e-signature', amount: '$4,850' },
      { title: 'First consultation',    meta: '14 Jun · In practice', amount: '—'      },
    ],
    invoiceLines: [
      { label: 'Treatment plan — 3 sessions', value: '$3,900' },
      { label: 'Materials',                   value: '$450'   },
      { label: 'Tax',                         value: '$500'   },
    ],
  },

  tr: {
    today: 'Bugün',
    live: 'Canlı',
    revenue: 'Aylık gelir',
    appointments: 'Randevular',
    thisWeek: 'Bu hafta',
    customerName: 'Ayşe Yılmaz',
    avatar: 'AY',
    customerSince: 'Müşteri · 2019’dan beri',
    active: 'Aktif',
    crmTabs: ['Geçmiş', 'Belgeler', 'Ödemeler', 'Notlar'],
    invoiceNo: 'Fatura #2026-0148',
    invoiceMeta: 'Ayşe Yılmaz · Vade 14 gün',
    paid: 'ÖDENDİ',
    total: 'Toplam',
    totalValue: '₺4.850',
    paidBy: 'Kartla ödendi · 12 Tem 14:32',
    website: 'Web sitesi',
    published: 'Yayında',
    siteBlocks: ['Üst menü', 'Hero', 'Hizmetler', 'Randevu', 'Alt bilgi'],
    nav: ['Pano', 'Takvim', 'Müşteriler', 'Faturalar', 'Web sitesi', 'Ekip', 'Ayarlar'],
    kpis: [
      { label: 'Randevu', value: '18',     delta: '+12%' },
      { label: 'Gelir',   value: '₺32,4K', delta: '+8%'  },
      { label: 'Yeni',    value: '7',      delta: '+3'   },
    ],
    days: [
      { name: 'Pzt', slots: [{ time: '09:00', who: 'Ayşe Y.', hot: false }, { time: '11:30', who: 'Mert K.', hot: false }] },
      { name: 'Sal', slots: [{ time: '10:00', who: 'Zeynep A.', hot: true }, { time: '15:00', who: 'Can T.', hot: false }] },
      { name: 'Çar', slots: [{ time: '09:30', who: 'Elif D.', hot: false }] },
      { name: 'Per', slots: [{ time: '13:00', who: 'Burak S.', hot: false }, { time: '16:30', who: 'Nil P.', hot: false }] },
    ],
    timeline: [
      { title: 'Kontrol randevusu',      meta: '12 Tem · Dr. Kaya', amount: '₺450'   },
      { title: 'Tedavi planı imzalandı', meta: '28 Haz · e-imza',   amount: '₺4.850' },
      { title: 'İlk görüşme',            meta: '14 Haz · Ofis',     amount: '—'      },
    ],
    invoiceLines: [
      { label: 'Tedavi planı — 3 seans', value: '₺3.900' },
      { label: 'Malzeme',                value: '₺450'   },
      { label: 'KDV %20',                value: '₺500'   },
    ],
  },
};
